import React, { Fragment } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';

import { FieldTableHeader } from 'components/_basic/Form/FieldTable2/components/FieldTableHeader';
import { FieldTableRow } from 'components/_basic/Form/FieldTable2/components/FieldTableRow';
import { FieldTableTotalRow } from 'components/_basic/Form/FieldTable2/components/FieldTableTotalRow';
import { FieldTableContainer } from 'components/_basic/Form/FieldTableContainer';
import { RadioControlled } from 'components/_basic/Form/Radio';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { CurrencyUnitSelector } from 'components/BusinessToolForm/components/CurrencyUnitSelector/CurrencyUnitSelector';
import { useGetPeriodOptions } from 'components/BusinessToolForm/constants/periods';
import { FullScreenWrapper } from 'components/BusinessToolForm/Sections/FinancialPlan/components/FullScreenWrapper';
import { useUnitChange } from 'components/BusinessToolForm/Sections/FinancialPlan/hooks/useUnitChange';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
  GLOBAL_PRODUCTS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { ISalesPlanProductTableProps } from './types';
import { prefix, REVENUE_PLAN_NAMES } from '../../../../utils/form';

import styles from './SalesPlanProductTable.module.scss';

const COLUMN_WIDTH = 110;

export const SalesPlanProductTable: React.FC<ISalesPlanProductTableProps> = ({
  tableName,
  fields,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { key } = useUnitChange();
  const periodOptions = useGetPeriodOptions();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const tableTotalName = concatWithDot(prefix, REVENUE_PLAN_NAMES.PLANNED_PRODUCTS_VOLUME_TOTAL);
  const otherIncomeName = concatWithDot(prefix, REVENUE_PLAN_NAMES.PLANNED_PRODUCTS_VOLUME_OTHER_INCOME);
  const titleWatchName = concatWithDot(GLOBAL_PRODUCTS_NAME, ':index', PRODUCT_NAMES.NAME);

  const columnsNames = getPeriodColumnsNames(period) ?? [];

  return (
    <FormSectionCard
      disabled={disabled}
      title={t('financialPlan.profitAndLossProjection.netSales.netSalesRevenue')}
      collapsible
    >
      <FullScreenWrapper
        title={t('financialPlan.profitAndLossProjection.netSales.netSalesRevenue')}
        disabled={disabled}
      >
        <Fragment key={key}>
          <Box className={styles.header}>
            <RadioControlled
              id="salesPlanProductTable.period"
              name={GLOBAL_PERIOD_NAME}
              control={control}
              options={periodOptions}
              disabled={disabled}
            />
            <CurrencyUnitSelector name={concatWithDot(prefix, REVENUE_PLAN_NAMES.CURRENCY)} disabled={disabled} />
          </Box>

          <FormSectionFilledCard>
            <FieldTableContainer>
              <FieldTableHeader
                name={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
                columnsNames={columnsNames}
                firstColumnWidth={COLUMN_WIDTH}
                disabled={disabled}
                editableColumn
                firstColumnClass={styles.sticky}
              />
              {fields.map((rowItem, index) => (
                <FieldTableRow
                  key={rowItem.id}
                  name={tableName}
                  rowIndex={index}
                  columnsNames={columnsNames}
                  watchRowTitleName={titleWatchName}
                  firstColumnWidth={COLUMN_WIDTH}
                  readOnly
                  disabled={disabled}
                  decimalScale={1}
                />
              ))}
              <FieldTableRow
                rowIndex={0}
                name={otherIncomeName}
                rowTitle={t('otherIncome')}
                columnsNames={columnsNames}
                firstColumnWidth={COLUMN_WIDTH}
                disabled={disabled}
              />

              <FieldTableTotalRow
                totalName={tableTotalName}
                columns={columnsNames}
                disabled={disabled}
                firstColumnWidth={COLUMN_WIDTH}
              />
            </FieldTableContainer>
          </FormSectionFilledCard>
        </Fragment>
      </FullScreenWrapper>
    </FormSectionCard>
  );
};
